import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Lock, CheckCircle2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { api, eur } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useStore } from "../context/StoreContext";
import { CartLine } from "../components/shop/CartLine";
import { langName } from "../lib/editions";

export const useVouchers = () => {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  useEffect(() => { if (user && user.role !== "admin" && user.onboarded) api.get("/community/me").then((r) => setCoupons(r.data.coupons.filter((c) => !c.used))).catch(() => setCoupons([])); }, [user]);
  return coupons;
};

export const OrderSummary = ({ count, subtotal, discount = 0, shipping, children }) => {
  const { tr } = useLang();
  const { free_shipping_de, prices_include_vat } = useStore();
  return (
    <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-6 space-y-3 text-sm" data-testid="order-summary">
      <h2 className="font-display text-2xl">{tr("Bestellübersicht", "Order summary")}</h2>
      <div className="flex justify-between"><span>{tr("Zwischensumme", "Subtotal")} ({count} {tr("Artikel", "items")})</span><span className="font-mono" data-testid="summary-subtotal">{eur(subtotal)}</span></div>
      {discount > 0 && <div className="flex justify-between text-[#6E3FB8]"><span>{tr("Gutschein", "Voucher")}</span><span className="font-mono" data-testid="summary-discount">−{eur(discount)}</span></div>}
      <div className="flex justify-between"><span>{tr("Versandkosten", "Shipping")}</span><span className="font-mono" data-testid="summary-shipping">{shipping === null ? tr("an der Kasse", "at checkout") : shipping === 0 ? tr("Kostenlos", "Free") : eur(shipping)}</span></div>
      {free_shipping_de && <p className="text-[11px] text-[#707783]">{tr(`Kostenloser Versand innerhalb Deutschlands ab ${eur(free_shipping_de)}`, `Free shipping within Germany from ${eur(free_shipping_de)}`)}</p>}
      <div className="flex justify-between items-end border-t pt-3"><span className="font-display text-xl">{tr("Gesamtsumme", "Total")}</span><span className="font-mono text-2xl font-semibold" data-testid="summary-total">{eur(subtotal - discount + (shipping || 0))}</span></div>
      <p className="text-[11px] text-[#707783]" data-testid="summary-vat">{prices_include_vat ? tr("inkl. gesetzl. MwSt. – die genaue MwSt. wird an der Kasse berechnet.", "incl. statutory VAT – exact VAT is calculated at checkout.") : tr("zzgl. MwSt.", "plus VAT")}</p>
      {children}
    </div>
  );
};

const Trust = () => {
  const { tr } = useLang();
  return (
    <ul className="rounded-2xl border border-[#120E1F]/5 bg-white p-5 space-y-2 text-sm" data-testid="cart-trust">
      {[tr("Originale Pokémon-Produkte", "Authentic Pokémon products"), tr("Sichere Zahlung über Stripe", "Secure payment via Stripe"), tr("Versand aus Deutschland", "Ships from Germany"), tr("Persönlicher Kundenservice", "Personal customer service")].map((x) => (
        <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E9E6B]" />{x}</li>))}
    </ul>
  );
};

export default function Cart() {
  const { items, count, subtotal, validate, notices } = useCart();
  const { tr, lang } = useLang();
  const { shipping, free_shipping_de, prelaunch } = useStore();
  const nav = useNavigate();
  const [checking, setChecking] = useState(false);
  const [removed, setRemoved] = useState([]);
  useEffect(() => { validate().then((r) => setRemoved(r.removed)).catch(() => {}); }, [validate]);
  const ship = items.length ? (subtotal >= (free_shipping_de || Infinity) ? 0 : shipping?.DE ?? null) : 0;
  const go = async () => {
    setChecking(true);
    try { const r = await validate(); setRemoved(r.removed); if (r.ok) nav("/checkout"); else toast.warning(tr("Dein Warenkorb wurde aktualisiert – bitte prüfe die Hinweise.", "Your cart was updated – please review the notices.")); }
    catch { toast.error(tr("Warenkorb konnte nicht geprüft werden.", "Couldn't check your cart.")); } finally { setChecking(false); }
  };
  return (
    <div className="bg-[#F7F5FB] min-h-screen" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><h1 className="font-display text-4xl sm:text-5xl">{tr("Dein Warenkorb", "Your cart")} <span className="text-xl text-[#707783] font-sans">({count} {tr("Artikel", "items")})</span></h1>
            <p className="text-sm text-[#707783] mt-1">{tr("Prüfe Sprache und Edition jedes Artikels, passe die Menge an oder entferne Produkte.", "Check the language and edition of each item, adjust quantities or remove products.")}</p></div>
          <Link to="/shop" className="btn-outline" data-testid="continue-shopping-btn"><ChevronLeft className="h-4 w-4" />{tr("Weiter einkaufen", "Continue shopping")}</Link>
        </div>
        {removed.map((r) => <p key={r.id} className="rounded-xl bg-[#F2C230]/15 text-[#8A5A00] text-sm px-4 py-3 flex gap-2" data-testid="cart-removed-notice"><Info className="h-4 w-4 shrink-0 mt-0.5" />{r.name_de}{langName(r.language, lang) ? ` (${langName(r.language, lang)})` : ""}: {r.notice}</p>)}
        {items.length === 0 ? <p className="text-center py-24 text-[#707783]" data-testid="cart-empty">{tr("Dein Warenkorb ist leer.", "Your cart is empty.")}</p> : (
          <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
            <div className="space-y-3" data-testid="cart-lines">{items.map((i) => <CartLine key={i.id} i={i} notice={notices[i.id]} />)}</div>
            <aside className="space-y-4 lg:sticky lg:top-36">
              <OrderSummary count={count} subtotal={subtotal} shipping={ship}>
                <button onClick={go} disabled={checking || prelaunch} className="btn-gold w-full h-12" data-testid="cart-page-checkout-btn">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" />{prelaunch ? tr("Bestellungen ab Launch", "Orders open at launch") : tr("Zur Kasse", "Checkout")}</>}</button>
                <p className="text-[11px] text-center text-[#707783]">{tr("Jolgen-Punkte-Gutscheine kannst du an der Kasse einlösen.", "Redeem Jolgen points vouchers at checkout.")}</p>
              </OrderSummary>
              <Trust />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
